import { apiClient } from './apiClient';
import { uiLogger } from '../utils/loggerInstance';
import { getInvitationFrontendUrl } from '../utils/firebaseUrl';
import {
  validateOrganizationData,
  validateMembershipArray,
  validateOrganizationStats,
  validateInvitationArray,
  validateRetentionSettings,
  validateOrganizationIntake,
  validateOrganizationIntakePayload,
  validateMessageResponse,
  validateModelUpgradeResponse,
  validateOrganizationOutputSettings,
  type Membership,
  type OrganizationStats,
  type OrganizationData,
  type Invitation,
  type RetentionSettings,
  type OrganizationIntake,
  type OrganizationIntakePayload,
  type OrganizationOutputSettings,
  type OrganizationOutputSettingsUpdate,
} from '../schemas/organization';
import type { AgenticBusinessRoleNullable } from '../schemas/agentic';

// Re-export types for backward compatibility
export type {
  Membership,
  OrganizationStats,
  OrganizationData,
  Invitation,
  RetentionSettings,
  OrganizationIntake,
  OrganizationIntakePayload,
  OrganizationOutputSettings,
  OrganizationOutputSettingsUpdate,
} from '../schemas/organization';

/**
 * Build invitation request body with frontend URL derivation
 * Derives frontend URL from Firebase auth domain (e.g., docufy-459922.firebaseapp.com -> https://docufy-459922.web.app)
 * This ensures invitation links use the correct production URL even when testing locally
 *
 * @param email - Optional email for create invitation
 * @param role - Optional role for create invitation
 * @returns Request body with frontend_url if available
 */
function buildInvitationRequestBody(
  email?: string,
  role?: 'admin' | 'member',
): { email?: string; role?: 'admin' | 'member'; frontend_url?: string } {
  const frontendUrl = getInvitationFrontendUrl();

  const requestBody: { email?: string; role?: 'admin' | 'member'; frontend_url?: string } = {};

  if (email) {
    requestBody.email = email;
  }
  if (role) {
    requestBody.role = role;
  }

  // Always include frontend_url if we derived it from Firebase (backend will use it for invitation links)
  if (frontendUrl) {
    requestBody.frontend_url = frontendUrl;
    uiLogger.debug('[buildInvitationRequestBody] Sending frontend_url to backend', { frontendUrl });
  } else {
    uiLogger.warn(
      '[buildInvitationRequestBody] Could not derive frontend URL from VITE_FIREBASE_AUTH_DOMAIN',
    );
  }

  return requestBody;
}

export const organizationService = {
  /**
   * Get the current user's organization. Returns null when the user has no organization
   * (e.g. individual user or left org). Does not throw on 200 + null.
   */
  getOrganization: async (): Promise<OrganizationData | null> => {
    const response = await apiClient.get<OrganizationData | null>('/api/v1/organizations/me');
    if (response.data == null) {
      return null;
    }
    const validation = validateOrganizationData(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid organization data: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  updateOrganization: async (
    orgId: string,
    data: {
      name?: string;
      description?: string;
      enabled_industries?: string[];
      enabled_ai_models?: string[];
    },
  ) => {
    const response = await apiClient.put(`/api/v1/organizations/${orgId}`, data);

    // First try to validate as organization data (expected response)
    const orgValidation = validateOrganizationData(response.data);
    if (orgValidation.success) {
      return orgValidation.data;
    }

    // Fallback: check if it's a generic message response
    const messageValidation = validateMessageResponse(response.data);
    if (messageValidation.success) {
      return messageValidation.data;
    }

    const orgErrors = orgValidation.error.issues.map((e) => e.message).join(', ');
    const messageErrors = messageValidation.error.issues.map((e) => e.message).join(', ');

    throw new Error(
      `Invalid update organization response: neither OrganizationData nor MessageResponse validation succeeded. ` +
        `OrganizationData errors: ${orgErrors}. MessageResponse errors: ${messageErrors}`,
    );
  },

  getMembers: async (orgId: string): Promise<Membership[]> => {
    const response = await apiClient.get<Membership[]>(`/api/v1/organizations/${orgId}/members`);
    const validation = validateMembershipArray(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid members data: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  updateMemberRole: async (orgId: string, userId: string, role: 'admin' | 'member') => {
    const response = await apiClient.put(`/api/v1/organizations/${orgId}/members/${userId}/role`, {
      role,
    });
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid update member role response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  updateMemberAgenticBusinessRole: async (
    orgId: string,
    userId: string,
    agenticBusinessRole: AgenticBusinessRoleNullable,
  ) => {
    const response = await apiClient.put(
      `/api/v1/organizations/${orgId}/members/${userId}/agentic-business-role`,
      { agentic_business_role: agenticBusinessRole },
    );
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid update member agentic profile response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  removeMember: async (orgId: string, userId: string) => {
    const response = await apiClient.delete(`/api/v1/organizations/${orgId}/members/${userId}`);
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid remove member response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  getStats: async (orgId: string): Promise<OrganizationStats> => {
    const response = await apiClient.get<OrganizationStats>(`/api/v1/organizations/${orgId}/stats`);
    const validation = validateOrganizationStats(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid stats data: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  transferOwnership: async (orgId: string, newOwnerEmail: string) => {
    const response = await apiClient.post(`/api/v1/organizations/${orgId}/transfer`, {
      new_owner_email: newOwnerEmail,
    });
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid transfer ownership response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  deleteOrganization: async (orgId: string) => {
    const response = await apiClient.delete(`/api/v1/organizations/${orgId}`);
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid delete organization response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  getLogo: async (orgId: string): Promise<Blob> => {
    const response = await apiClient.get(`/api/v1/organizations/${orgId}/logo`, {
      responseType: 'blob',
    });
    // Blob responses don't need validation
    return response.data;
  },

  uploadLogo: async (orgId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/api/v1/organizations/${orgId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid upload logo response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  deleteLogo: async (orgId: string) => {
    const response = await apiClient.delete(`/api/v1/organizations/${orgId}/logo`);
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid delete logo response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  getRetentionSettings: async (orgId: string): Promise<RetentionSettings> => {
    const response = await apiClient.get<RetentionSettings>(
      `/api/v1/organizations/${orgId}/retention-settings`,
    );
    const validation = validateRetentionSettings(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid retention settings: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  updateRetentionSettings: async (orgId: string, settings: Partial<RetentionSettings>) => {
    const response = await apiClient.put(
      `/api/v1/organizations/${orgId}/retention-settings`,
      settings,
    );
    const validation = validateRetentionSettings(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid update retention settings response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  getOutputSettings: async (orgId: string): Promise<OrganizationOutputSettings> => {
    const response = await apiClient.get<OrganizationOutputSettings>(
      `/api/v1/organizations/${orgId}/output-settings`,
    );
    const validation = validateOrganizationOutputSettings(response.data);
    if (!validation.success) {
      uiLogger.error('Invalid output settings response from API', {
        issues: validation.error.issues,
      });
      throw new Error('Could not load layout settings. Please try again.');
    }
    return validation.data;
  },

  updateOutputSettings: async (
    orgId: string,
    settings: OrganizationOutputSettingsUpdate,
  ): Promise<OrganizationOutputSettings> => {
    const response = await apiClient.put<OrganizationOutputSettings>(
      `/api/v1/organizations/${orgId}/output-settings`,
      settings,
    );
    const validation = validateOrganizationOutputSettings(response.data);
    if (!validation.success) {
      uiLogger.error('Invalid update output settings response from API', {
        issues: validation.error.issues,
      });
      throw new Error('Could not save layout settings. Please try again.');
    }
    return validation.data;
  },

  getIntake: async (orgId: string): Promise<OrganizationIntake> => {
    const response = await apiClient.get<OrganizationIntake>(
      `/api/v1/organizations/${orgId}/intake`,
    );
    const validation = validateOrganizationIntake(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid organization intake: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  upsertIntake: async (
    orgId: string,
    payload: OrganizationIntakePayload,
  ): Promise<OrganizationIntake> => {
    const payloadValidation = validateOrganizationIntakePayload(payload);
    if (!payloadValidation.success) {
      throw new Error(
        `Invalid intake payload: ${payloadValidation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    const response = await apiClient.put<OrganizationIntake>(
      `/api/v1/organizations/${orgId}/intake`,
      payloadValidation.data,
    );
    const validation = validateOrganizationIntake(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid organization intake response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  createInvitation: async (orgId: string, email: string, role: 'admin' | 'member') => {
    const requestBody = buildInvitationRequestBody(email, role);

    const response = await apiClient.post(
      `/api/v1/organizations/${orgId}/invitations`,
      requestBody,
    );
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid create invitation response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  getPendingInvitations: async (orgId: string): Promise<Invitation[]> => {
    const response = await apiClient.get<Invitation[]>(
      `/api/v1/organizations/${orgId}/invitations`,
    );
    const validation = validateInvitationArray(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid invitations data: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  resendInvitation: async (orgId: string, invitationId: string) => {
    const requestBody = buildInvitationRequestBody();

    const response = await apiClient.post(
      `/api/v1/organizations/${orgId}/invitations/${invitationId}/resend`,
      requestBody,
    );
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid resend invitation response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  revokeInvitation: async (orgId: string, invitationId: string) => {
    const response = await apiClient.delete(
      `/api/v1/organizations/${orgId}/invitations/${invitationId}`,
    );
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid revoke invitation response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  acceptInvitation: async (token: string) => {
    const response = await apiClient.post('/api/v1/invitations/accept', { token });
    const validation = validateMessageResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid accept invitation response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  validateInvitation: async (token: string) => {
    const response = await apiClient.get(`/api/v1/invitations/validate?token=${token}`);
    // Validation endpoint might return various shapes, keep flexible
    return response.data;
  },

  requestModelUpgrade: async (
    orgId: string,
    modelName: string,
    reason: string,
    priceId: string | null,
  ) => {
    const response = await apiClient.post(`/api/v1/organizations/${orgId}/model-upgrade-request`, {
      model_name: modelName,
      reason,
      price_id: priceId,
    });
    const validation = validateModelUpgradeResponse(response.data);
    if (!validation.success) {
      throw new Error(
        `Invalid request model upgrade response: ${validation.error.issues.map((e) => e.message).join(', ')}`,
      );
    }
    return validation.data;
  },

  isOrganizationData: (data: unknown): data is OrganizationData => {
    return validateOrganizationData(data).success;
  },
};
